import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  ArrowLeft,
  Gauge,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Pencil,
  Power,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { VerticalSubNav, type VerticalSubNavItem } from '@/components/VerticalSubNav';
import { LoadingState } from '@/components/LoadingState';
import { EmptyState } from '@/components/EmptyState';
import { LabelledField } from '@/components/LabelledField';
import { useCustomer, useResetPassword, useUpdateCustomer } from '@/features/admin-customers/api';
import { generatePassword } from '@/features/admin-customers/password';
import { companySlugSchema } from '@/features/admin-customers/schema';
import { ProductScopePicker } from '@/features/admin-customers/components/ProductScopePicker';
import { IssueListTable } from '@/features/issues/components/IssueListTable';
import { UploadsListTable } from '@/features/uploads/components/UploadsListTable';
import { AuditLogTable } from '@/features/admin/components/AuditLogTable';
import { SitesAndWells } from '@/features/admin-wells/components/SitesAndWells';
import { ApiError } from '@/lib/api/client';
import { isDataUploadsEnabled } from '@/lib/features';

// Simple in-page email schema — mirrors the backend format check so the admin
// gets a fast local error before the PATCH round-trips.
const emailSchema = z.string().email('Must be a valid email').max(255);

type SectionId = 'overview' | 'issues' | 'sites' | 'uploads' | 'activity';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: customer, isLoading, isError, error } = useCustomer(id);
  const updateMutation = useUpdateCustomer(id ?? '');
  const resetMutation = useResetPassword(id ?? '');

  const [section, setSection] = useState<SectionId>('overview');
  const [statusDialog, setStatusDialog] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [slugDialog, setSlugDialog] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  // Local buffer for the scope picker — mirrors customer.productScopes but
  // only PATCHes when the admin hits Save. Prevents a checkbox tick from
  // firing a mutation per click (which would 400 mid-edit for the empty-set
  // intermediate state on any config that policed emptiness).
  const [scopeBuffer, setScopeBuffer] = useState<string[]>([]);
  const [scopeDirty, setScopeDirty] = useState(false);

  useEffect(() => {
    // Re-sync when the query refetches so the picker reflects server truth on
    // navigation / cache invalidations. `scopeDirty` guards mid-edit stomping.
    if (customer && !scopeDirty) {
      setScopeBuffer(customer.productScopes);
    }
  }, [customer, scopeDirty]);

  // Chunk 4 hid the customer /uploads surface behind a flag; here we hide the
  // *admin* Uploads section under the same flag so the two ship consistently.
  // If the flag flips on later, the section reappears automatically.
  const uploadsEnabled = isDataUploadsEnabled();

  const navItems: VerticalSubNavItem[] = useMemo(
    () => [
      { id: 'overview', label: 'Overview', Icon: LayoutDashboard },
      { id: 'issues', label: 'Issues', Icon: ListChecks },
      { id: 'sites', label: 'Sites & wells', Icon: Gauge },
      { id: 'uploads', label: 'Uploads', Icon: Upload, hidden: !uploadsEnabled },
      { id: 'activity', label: 'Recent activity', Icon: Activity },
    ],
    [uploadsEnabled],
  );

  if (isLoading) {
    return <LoadingState rows={5} className="mx-auto max-w-5xl" />;
  }
  if (isError || !customer) {
    return (
      <EmptyState
        title="Couldn't load customer"
        description={error instanceof Error ? error.message : 'Please try again.'}
        className="mx-auto max-w-5xl"
      />
    );
  }

  const toggleActive = async () => {
    try {
      await updateMutation.mutateAsync({ isActive: !customer.isActive });
      toast.success(customer.isActive ? 'Customer deactivated' : 'Customer activated');
      setStatusDialog(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Update failed');
    }
  };

  const onResetSubmit = async () => {
    if (resetPassword.length < 12) {
      toast.error('Password must be at least 12 characters');
      return;
    }
    try {
      await resetMutation.mutateAsync({ password: resetPassword, temporary: true });
      try {
        await navigator.clipboard.writeText(resetPassword);
        toast.success('Password reset — new password copied to clipboard');
      } catch {
        toast.success('Password reset (clipboard unavailable — copy manually)');
      }
      setResetDialog(false);
      setResetPassword('');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Reset failed');
    }
  };

  const onScopeChange = (next: string[]) => {
    setScopeBuffer(next);
    setScopeDirty(true);
  };

  const onScopeSave = async () => {
    try {
      await updateMutation.mutateAsync({ productScopes: scopeBuffer });
      setScopeDirty(false);
      toast.success('Product scopes updated');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update scopes');
    }
  };

  const onScopeReset = () => {
    setScopeBuffer(customer.productScopes);
    setScopeDirty(false);
  };

  const openEmailDialog = () => {
    setEmailInput(customer.email);
    setEmailError(null);
    setEmailDialog(true);
  };

  const onEmailSubmit = async () => {
    const trimmed = emailInput.trim();
    if (trimmed === customer.email) {
      setEmailDialog(false);
      return;
    }
    const parsed = emailSchema.safeParse(trimmed);
    if (!parsed.success) {
      setEmailError(parsed.error.issues[0]?.message ?? 'Invalid email');
      return;
    }
    try {
      await updateMutation.mutateAsync({ email: trimmed });
      toast.success(`Email updated to ${trimmed}`);
      setEmailDialog(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update email');
    }
  };

  const openSlugDialog = () => {
    setSlugInput(customer.companySlug);
    setSlugError(null);
    setSlugDialog(true);
  };

  const onSlugSubmit = async () => {
    const trimmed = slugInput.trim();
    if (trimmed === customer.companySlug) {
      setSlugDialog(false);
      return;
    }
    const parsed = companySlugSchema.safeParse(trimmed);
    if (!parsed.success) {
      setSlugError(parsed.error.issues[0]?.message ?? 'Invalid slug');
      return;
    }
    try {
      await updateMutation.mutateAsync({ companySlug: trimmed });
      toast.success(`Slug updated to ${trimmed}`);
      setSlugDialog(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update slug');
    }
  };

  return (
    <div
      className="mx-auto max-w-5xl space-y-4"
      data-testid="admin-customer-detail-root"
      data-customer-id={customer.id}
    >
      <div>
        <Link
          to="/admin/customers"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to customers
        </Link>
      </div>

      <PageHeader
        title={<span data-testid="admin-customer-detail-name">{customer.name}</span>}
        subtitle={
          <span className="inline-flex items-center gap-2">
            <span data-testid="admin-customer-detail-email">{customer.email}</span>
            <button
              type="button"
              onClick={openEmailDialog}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              aria-label="Edit email"
              data-testid="admin-customer-detail-edit-email"
            >
              <Pencil className="h-3 w-3" />
              Edit email
            </button>
          </span>
        }
        meta={
          <>
            <code className="rounded bg-muted px-1.5 py-0.5">customer:{customer.companySlug}</code>
            <button
              type="button"
              onClick={openSlugDialog}
              className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              aria-label="Edit slug"
            >
              <Pencil className="h-3 w-3" />
              Edit slug
            </button>
          </>
        }
        status={
          customer.isActive ? (
            <StatusBadge
              variant="success"
              label="Active"
              data-testid="admin-customer-detail-status"
              data-status="active"
            />
          ) : (
            <StatusBadge
              variant="neutral"
              label="Inactive"
              data-testid="admin-customer-detail-status"
              data-status="inactive"
            />
          )
        }
      />

      <div className="grid grid-cols-[220px_1fr] gap-6">
        <VerticalSubNav
          items={navItems}
          activeId={section}
          onSelect={(id) => setSection(id as SectionId)}
        />

        <div className="min-w-0 space-y-4">
          {section === 'overview' && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  <Button
                    variant={customer.isActive ? 'destructive' : 'default'}
                    onClick={() => setStatusDialog(true)}
                    data-testid="admin-customer-detail-toggle-active"
                  >
                    <Power className="h-4 w-4" />
                    {customer.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="outline" onClick={() => setResetDialog(true)}>
                    <KeyRound className="h-4 w-4" />
                    Reset password
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                    Product scopes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ProductScopePicker
                    value={scopeBuffer}
                    onChange={onScopeChange}
                    disabled={updateMutation.isPending}
                  />
                  {scopeDirty && (
                    <div className="flex items-center justify-end gap-2 border-t pt-3">
                      <Button variant="ghost" size="sm" onClick={onScopeReset}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void onScopeSave()}
                        disabled={updateMutation.isPending}
                        data-testid="admin-customer-detail-save-scopes"
                      >
                        {updateMutation.isPending ? 'Saving…' : 'Save scopes'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {section === 'issues' && (
            <Card>
              <CardContent className="pt-6">
                <IssueListTable isAdmin={true} customerIdFilter={customer.id} />
              </CardContent>
            </Card>
          )}

          {section === 'sites' && (
            <Card>
              <CardContent className="pt-6">
                <SitesAndWells customerId={customer.id} />
              </CardContent>
            </Card>
          )}

          {section === 'uploads' && uploadsEnabled && (
            <Card>
              <CardContent className="pt-6">
                <UploadsListTable isAdmin={true} customerIdFilter={customer.id} />
              </CardContent>
            </Card>
          )}

          {section === 'activity' && (
            <Card>
              <CardContent className="space-y-3 pt-6">
                <AuditLogTable
                  showFilters={false}
                  defaultCustomerIdFilter={customer.id}
                  limit={25}
                />
                <div className="text-right">
                  <Button variant="link" asChild className="px-0">
                    <Link to={`/admin/audit-log?customerId=${customer.id}`}>
                      View full activity log →
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {customer.isActive ? 'Deactivate customer?' : 'Activate customer?'}
            </DialogTitle>
            <DialogDescription>
              {customer.isActive
                ? 'They will no longer be able to sign in to the portal.'
                : 'They will be able to sign in to the portal again.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)}>
              Cancel
            </Button>
            <Button
              variant={customer.isActive ? 'destructive' : 'default'}
              onClick={() => void toggleActive()}
              disabled={updateMutation.isPending}
              data-testid="admin-customer-detail-toggle-confirm"
            >
              {updateMutation.isPending ? 'Working…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>
              The customer will be required to change it on next login.
            </DialogDescription>
          </DialogHeader>
          <LabelledField label="New password" htmlFor="reset-pw">
            <div className="flex gap-2">
              <Input
                id="reset-pw"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setResetPassword(generatePassword(16))}
              >
                <RefreshCw className="h-4 w-4" />
                Generate
              </Button>
            </div>
          </LabelledField>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => void onResetSubmit()} disabled={resetMutation.isPending}>
              {resetMutation.isPending ? 'Resetting…' : 'Reset password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={slugDialog} onOpenChange={setSlugDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit company slug</DialogTitle>
            <DialogDescription>
              Changing the slug breaks correlation with previously-filed Gitea issues — their
              existing <code>customer:{customer.companySlug}</code> labels will no longer match new
              submissions under the new slug.
            </DialogDescription>
          </DialogHeader>
          <LabelledField label="New slug" htmlFor="slug-input" error={slugError ?? undefined}>
            <Input
              id="slug-input"
              value={slugInput}
              onChange={(e) => {
                setSlugInput(e.target.value);
                setSlugError(null);
              }}
              placeholder="e.g. archer"
              aria-invalid={!!slugError}
            />
          </LabelledField>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlugDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => void onSlugSubmit()} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving…' : 'Save slug'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailDialog} onOpenChange={setEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit customer email</DialogTitle>
            <DialogDescription>
              Updates both the portal record (used for the Gitea issue-origin footer) and the
              customer&apos;s Keycloak account.
            </DialogDescription>
          </DialogHeader>
          <LabelledField label="New email" htmlFor="email-input" error={emailError ?? undefined}>
            <Input
              id="email-input"
              type="email"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setEmailError(null);
              }}
              placeholder="new@example.com"
              aria-invalid={!!emailError}
              data-testid="admin-customer-detail-email-input"
            />
          </LabelledField>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void onEmailSubmit()}
              disabled={updateMutation.isPending}
              data-testid="admin-customer-detail-email-submit"
            >
              {updateMutation.isPending ? 'Saving…' : 'Save email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
